import { yupResolver } from '@hookform/resolvers/yup';
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
// @mui
import LoadingButton from '@mui/lab/LoadingButton';
import { Box, Button } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
// routes
import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';
import { useRouter, useSearchParams } from 'src/routes/hooks';
// config
import { FORGOT_PASSWORD } from 'src/config-global';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
// components
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import FormProvider, { RHFTextField } from 'src/components/hook-form';
import Iconify from 'src/components/iconify';
import { useLoginMutation } from "../../../store/Reducer/auth";
import { selectDeviceId, setUser } from 'src/store/slices/userSlice';


export default function JwtLoginPasswordView() {

  const [userTimeZone, setUserTimeZone] = useState('');

  const searchParams = useSearchParams();

  const email = searchParams.get('email');

  const dispatch = useDispatch();

  const [loginData] = useLoginMutation();

  const router = useRouter();

  const password = useBoolean();

  const deviceId = useSelector(selectDeviceId);

  useEffect(() => {
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    setUserTimeZone(userTimeZone);
  }, []);

  const LoginSchema = Yup.object().shape({
    email: Yup.string().required('Email is required').email('Email must be a valid email address'),
    password: Yup.string().min(8,
      'Password must be at least 8 characters long'
    ).required('Password is required'),
  });

  const defaultValues = {
    email: email || '',
    password: '',
  };

  const methods = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = handleSubmit(async (data) => {
    try {
      const payload = {
        email: data.email,
        password: data.password,
        device_type: "web",
        device_id: deviceId || 'abc',
        timezone: userTimeZone,
      }
      const response = await loginData(payload).unwrap();
      if (response.error) {
        return toast.error(response?.error?.data?.message);
      }
      const user = response.data;

      dispatch(setUser(user));
      if (user.user_type === '2') {
        router.replace(paths.adminDashboard.root);
      } else if (user.user_type === '3' || user.user_type === '4') {
        router.replace(paths.dashboard.file);
      } else if (user.user_type === '1') {
        router.replace(paths.superAdminDashboard.root);
      }
      else {
        toast.error('Invalid user type');
      }
    } catch (error) {
      console.error(error);
      toast.error(error?.data?.message || 'Failed to login');
    }
  });


  const renderHead = (
    <Stack spacing={2} sx={{ mb: 2 }}>
      <Typography variant="h4">Sign in to SiteSnap</Typography>
    </Stack>
  );

  const renderForm = (
    <Stack spacing={2.5} >
      <RHFTextField
        name="password"
        label="Password"
        type={password.value ? 'text' : 'password'}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={password.onToggle} edge="end">
                <Iconify icon={password.value ? 'solar:eye-bold' : 'solar:eye-closed-bold'} />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <Link onClick={() => router.push(FORGOT_PASSWORD)} variant="body2" color="inherit" underline="always" sx={{ alignSelf: 'flex-end', cursor: 'pointer' }}>
        Forgot password?
      </Link>
      <LoadingButton
        fullWidth
        color="primary"
        size="large"
        type="submit"
        variant="contained"
        loading={isSubmitting}
      >
        Login
      </LoadingButton>
    </Stack>
  );

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      {renderHead}
      {renderForm}
    </FormProvider>
  );
}
